
import VueApp from '@/@types/app/VueApp';
import SmartContentLoader from '@/components/article-editor/EditorNavbar/SmartContentLoader/SmartContentLoader.vue';
import TranscriptionSubtitle from '@/components/layout/sidebars/TranscriptionSidebar/TranscriptionSubtitle/TranscriptionSubtitle.vue';
import handleOutsideClickDirective from '@/directives/handleOutsideClickDirective';
import logger from '@/other/Logger';
import * as routerNames from '@/routerNames';
import apiService from '@/services/ApiService';
import articleEditService from '@/services/article/ArticleEditService';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import notificationService from '@/services/NotificationService';
import aiGenerationPollingService from '@/services/AiGenerationPollingService';
import { AiGenerationType } from '@/services/api-providers/AiGenerationServiceProvider';
import store from '@/store';
import { Component, Watch } from 'vue-property-decorator';

@Component({
  components: { SmartContentLoader, TranscriptionSubtitle },
  directives: {
    'click-outside': handleOutsideClickDirective(
      () => {
        if (store.state.transcriptionSidebar.isVisible) {
          store.commit('transcriptionSidebarSetIsVisible', false);
        }
      },
      ['transcription-view-button'],
      ['transcription-view-button', 'transcription-generate-button', 'transcription-generation-loader'],
      'transcription-sidebar'
    ),
  },
})
export default class TranscriptionSidebar extends VueApp {
  loading = false;

  isTranscriptionGenerating = false;
  transcriptionGenerationProgress = 0;

  get isExtensionBannerVisible() {
    return this.$store.state.ui.isExtensionBannerVisible;
  }

  get isMobile(): boolean {
    return mobileResponsiveService.isMobileView;
  }

  get isArticleEditPage() {
    return [
      routerNames.ARTICLE_CREATE,
      routerNames.ARTICLE_EDIT,
      routerNames.ARTICLE_VERSION_EDIT,
      routerNames.DRAFT_EDIT,
    ].includes(this.$route.name);
  }

  get transcription() {
    return this.$store.state.transcriptionSidebar.transcription;
  }

  get styles() {
    const diff = this.isExtensionBannerVisible && !this.isMobile ? '100' : '60';
    return {
      top: diff + 'px',
      height: `calc(100vh - ${diff}px)`,
    };
  }

  get isVideoArticle() {
    const article = articleEditService.articleEditor?.article;
    return article?.isVideoArticle() ?? false;
  }

  onClickClose() {
    this.$store.commit('transcriptionSidebarSetIsVisible', false);
  }

  async onStartGeneration() {
    this.isTranscriptionGenerating = true;
    this.transcriptionGenerationProgress = 0;

    const article = articleEditService.articleEditor.article;

    try {
      await aiGenerationPollingService.generateTranscription(
        { articleId: article.id },
        {
          onProgress: (progress) => {
            this.transcriptionGenerationProgress = progress;
          },
          onCompleted: (result) => {
            if (article.id === articleEditService.articleEditor.article.id && this.isArticleEditPage) {
              let transcription =
                typeof result.transcription === 'string' ? JSON.parse(result.transcription) : result.transcription;

              // Flatten nested array if necessary (backend returns [[{...}]] instead of [{...}])
              if (Array.isArray(transcription) && transcription.length > 0 && Array.isArray(transcription[0])) {
                transcription = transcription.flat();
              }

              this.$store.commit('transcriptionSidebarSetTranscription', transcription);
            }
            this.isTranscriptionGenerating = false;
            this.transcriptionGenerationProgress = 0;
          },
          onError: (error) => {
            logger.error(`Transcription generation failed: ${error}`);
            notificationService.error(error);
            this.isTranscriptionGenerating = false;
            this.transcriptionGenerationProgress = 0;
          },
        }
      );
    } catch (err: any) {
      logger.error(`Failed to start transcription generation: ${err?.message || err}`);
      this.isTranscriptionGenerating = false;
      this.transcriptionGenerationProgress = 0;
    }
  }

  async onCancelGenerating() {
    // Only cancel if there's an active generation
    if (!this.isTranscriptionGenerating) {
      return;
    }

    const article = articleEditService.articleEditor.article;

    this.isTranscriptionGenerating = false;
    this.transcriptionGenerationProgress = 0;

    try {
      await aiGenerationPollingService.cancel(AiGenerationType.TRANSCRIPTION, article.id);
    } catch (err: any) {
      logger.error(`Failed to cancel transcription generation: ${err?.message || err}`);
    }
  }

  async loadTranscription() {
    if (this.isTranscriptionGenerating) return;
    this.loading = true;
    const article = articleEditService.articleEditor.article;

    if (!this.isArticleEditPage || !article) return;

    this.$store.commit('transcriptionSidebarSetTranscription', []);

    try {
      const resSubtitles = await apiService.article.getTranscription(article.id);
      const subtitles = resSubtitles.data.transcription;

      if (subtitles.length) {
        const parsedSubtitles = JSON.parse(subtitles);
        this.$store.commit('transcriptionSidebarSetTranscription', parsedSubtitles);
      } else {
        this.$store.commit('transcriptionSidebarSetTranscription', []);
      }
    } catch (err) {
      logger.error('Failed to load transcription for the article');
      this.$store.commit('transcriptionSidebarSetTranscription', []);
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route.path', { immediate: true })
  closeSidebarOnRouteChange(newPath: string, oldPath: string) {
    // Only reset if we're actually navigating to a different article/page
    // Don't reset when just switching modes (notes <-> video) on the same article

    // Extract article/draft ID from the path to check if we're still on the same article
    const extractId = (path: string) => {
      if (!path) return null;
      // Match patterns like /article/123/edit/video or /draft/456/edit/notes
      const match = path.match(/\/(article|draft)\/([^/]+)/);
      return match ? match[2] : null;
    };

    const newId = extractId(newPath);
    const oldId = extractId(oldPath || '');

    // If article/draft ID changed or we left the article edit page, reset everything
    if (newId !== oldId) {
      this.$store.commit('transcriptionSidebarSetTranscription', []);
      this.$store.commit('transcriptionSidebarSetIsVisible', false);
      this.onCancelGenerating();
    }
  }

  @Watch('$store.state.transcriptionSidebar.isVisible')
  onVisibleChange(isVisible: boolean) {
    if (isVisible) {
      this.loadTranscription();
    }
  }
}
